import { SvelteMap } from "svelte/reactivity";
import type { Id, GalleryImage } from "../models";

const storageKey = "favorites-v2";
const legacyStorageKey = "favorites-v1";

export class FavoriteStore {
	private items = new SvelteMap<Id, GalleryImage>();

	constructor() {
		try {
			var raw = localStorage.getItem(storageKey);
			if (raw != null) {
				for (var item of JSON.parse(raw) as GalleryImage[]) {
					this.items.set(item.id, item);
				}
			} else {
				for (var legacyItem of loadLegacyItems()) {
					this.items.set(legacyItem.id, legacyItem);
				}
				if (this.items.size > 0) {
					this.persist();
				}
			}
		} catch {}
	}

	has(id: Id): boolean {
		return this.items.has(id);
	}

	get size(): number {
		return this.items.size;
	}

	add(item: GalleryImage) {
		this.items.set(item.id, item);
		this.persist();
	}

	delete(id: Id) {
		this.items.delete(id);
		this.persist();
	}

	clear() {
		this.items.clear();
		this.persist();
	}

	keys(): IterableIterator<Id> {
		return this.items.keys();
	}

	[Symbol.iterator](): IterableIterator<[Id, GalleryImage]> {
		return this.items[Symbol.iterator]();
	}

	getUrl(baseUrl: string): URL {
		var url = new URL("", baseUrl);
		url.hash = `#!${[...this.items.keys()].join(",")}`;
		return url;
	}

	private persist() {
		try {
			localStorage.setItem(storageKey, JSON.stringify([...this.items.values()]));
		} catch {}
	}
}

// One-time migration from @macfja/svelte-persistent-store, which wrapped the map in a
// @macfja/serializer envelope: { data: [[<ref>, key, value], ...], "#$@__constructor__": "Map" }
// with "#$@__reference__" bookkeeping keys injected into every serialized object.
function loadLegacyItems(): GalleryImage[] {
	var raw = localStorage.getItem(legacyStorageKey);
	if (raw == null) {
		return [];
	}
	localStorage.removeItem(legacyStorageKey);

	var parsed = JSON.parse(raw);
	if (parsed?.["#$@__constructor__"] !== "Map" || !Array.isArray(parsed.data)) {
		return [];
	}

	var items: GalleryImage[] = [];
	for (let entry of parsed.data) {
		if (!Array.isArray(entry)) {
			continue;
		}
		var value = entry[entry.length - 1];
		if (value != null && typeof value === "object") {
			items.push(stripSerializerKeys(value) as GalleryImage);
		}
	}
	return items;
}

function stripSerializerKeys(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map(stripSerializerKeys);
	}
	if (value != null && typeof value === "object") {
		for (let [key, propValue] of Object.entries(value)) {
			if (key.startsWith("#$@__")) {
				delete (value as Record<string, unknown>)[key];
			} else {
				stripSerializerKeys(propValue);
			}
		}
	}
	return value;
}

export const store = new FavoriteStore();
